import { CompanySettings, DateUtil, ModelRow } from "@mcleod/core";

const ORDER_STATUS_VOID = "V";
const ORDER_FILTER_TYPE_STATES = "S";
const ORDER_FILTER_TYPE_ZIP_CODE = "C";
const ORDER_FILTER_TYPE_LOCATION = "L";
const ORDER_FILTER_TYPE_ZONE = "Z";
const MOVEMENT_STATUS_DELIVERED = "D";

export class PartialShipmentPlanningFilter {

    private filterRow: ModelRow;

    constructor(filterRow: ModelRow) {
        this.filterRow = filterRow;
    }

    public shouldHideRow(data: ModelRow, isOrderDataSource: boolean): boolean {
        const hideRow = true;
        const { inboundType, inboundFilter, outboundType, outboundFilter } = this.inboundOutBoundValues();
        const companySettings = CompanySettings.get();
        const isAssetCompanyType = companySettings?.is_asset;
        const isBrokerageCompanyType = companySettings?.is_brokerage;
        const isBothCompanyType = isAssetCompanyType && isBrokerageCompanyType;
        const isBrokerageMove = data.get("brokerage", false);
        const isShellManifest = data.getBoolean("is_shell_manifests", false);
        const shouldIncludeByCompanyType = (isAssetCompanyType && !isBrokerageMove) || (isBrokerageCompanyType && isBrokerageMove) || isBothCompanyType;
        if (!isShellManifest) {
            if (!shouldIncludeByCompanyType) {
                return hideRow;
            }
        }
        if (this.isVoidOrder(data)) {
            return hideRow;
        }
        if (this.shouldExcludeByScheduleEarly(data, "origin")) {
            return hideRow;
        }
        if (this.shouldExcludeByScheduleEarly(data, "destination")) {
            return hideRow;
        }
        if (this.shouldExcludeTruckLoadOrder(data)) {
            return hideRow;
        }
        if (this.noMatchFilter(inboundType, inboundFilter, data, "destination")) {
            return hideRow;
        }
        if (this.noMatchFilter(outboundType, outboundFilter, data, "origin")) {
            return hideRow;
        }
        if (this.noMatchFilterRevenueCodes(data)) {
            return hideRow;
        }
        if (isOrderDataSource && !data.isNull("manifest_id")) {
            return hideRow;
        }
        if (!isOrderDataSource && MOVEMENT_STATUS_DELIVERED === data?.get("move_status")) {
            return hideRow;
        }
        return false;
    }

    private shouldExcludeByScheduleEarly(data: ModelRow, prefix: "origin" | "destination"): boolean {
        const ordersDateByProfile = this.getOrdersDate();
        const scheduleEarly = DateUtil.justDate(data.getDate(`${prefix}_schedule_early`));
        return scheduleEarly > ordersDateByProfile;
    }

    private getOrdersDate(): Date {
        const daysOut = this.filterRow?.getNumber("order_days", 0);
        const orderDate = DateUtil.justDate(new Date());
        orderDate.setDate(orderDate.getDate() + daysOut);
        return DateUtil.justDate(orderDate);
    }

    private inboundOutBoundValues() {
        const outboundType: string = this.filterRow.get("order_outbndtype");
        const outboundFilter: string = this.filterRow.get("order_outbndfltr");
        const inboundType: string = this.filterRow.get("order_inbndtype");
        const inboundFilter: string = this.filterRow.get("order_inbndfltr");
        return { inboundType, inboundFilter, outboundType, outboundFilter };
    }

    private noMatchFilterRevenueCodes(data: ModelRow): boolean {
        const profileRevenueCodes = this.filterRow.get("revenue_code");
        if (profileRevenueCodes) {
            const filterRevenueCodes = profileRevenueCodes.split(",");
            return !filterRevenueCodes.includes(data.get("revenue_code"));
        }
        return false;
    }

    private noMatchFilter(orderType: any, orderFilter: any, data: ModelRow, prefix: string): boolean {
        if (!orderFilter) return false;

        const isStateFilterType = ORDER_FILTER_TYPE_STATES === orderType;
        const isZipCodeFilterType = ORDER_FILTER_TYPE_ZIP_CODE === orderType;
        const isLocationCodeFilterType = ORDER_FILTER_TYPE_LOCATION === orderType;
        const isZoneCodeFilterType = ORDER_FILTER_TYPE_ZONE === orderType;

        if (isStateFilterType) {
            const modelState = data.get(`${prefix}_state`, "");
            const filterStates = orderFilter.split(",");
            return !filterStates.includes(modelState);
        }

        if (isLocationCodeFilterType) {
            const modellocation = data.get(`${prefix}_stop_id`, "");
            const filterLocation = orderFilter.split(",");
            return !filterLocation.includes(modellocation);
        }

        if (isZipCodeFilterType) {
            const modelZip = data.get(`${prefix}_zip_code`, "");
            return orderFilter !== modelZip;
        }

        if (isZoneCodeFilterType) {
            const modelZone = data.get(`${prefix}_zone_id`);
            const filterZone = orderFilter.split(",");
            return !filterZone.includes(modelZone);
        }

        return false;
    }

    private isVoidOrder(data: ModelRow): boolean {
        return data.get("move_status") === ORDER_STATUS_VOID;
    }

    private shouldExcludeTruckLoadOrder(orderData: ModelRow): boolean {
        const excludeTruckLoadOrders = this.filterRow.get("truckload") === "N";
        return excludeTruckLoadOrders && orderData.get("is_order_truckload", false);
    }

}
